import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, Tag, Search, ImageOff, Check, X, AlertCircle, CheckCircle, Plus, ExternalLink, Wand2, Trash2, Sparkles } from 'lucide-react';
import { Button } from './ui/button';
import BrandLogoFetchCard from './BrandLogoFetchCard';
import { Input } from './ui/input';
import { brandsAPI, uploadsAPI } from '../services/api';
import { loadBrandLogos, brandPath } from '../services/brandLogos';
import BrandSeoForm, { seoChecks } from './BrandSeoForm';

const EMPTY = { logo: '', description: '', main_product: '', main_keyword: '', secondary_keywords: [], h1: '', seo_title: '', meta_description: '', slug: '', excerpt: '', tags: [], faq: [] };

const toForm = (b) => ({
  ...EMPTY,
  ...Object.fromEntries(Object.keys(EMPTY).map(k => [k, b[k] ?? EMPTY[k]])),
});

const AdminBrandsPanel = () => {
  const [brands, setBrands] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [newName, setNewName] = useState('');
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [generatingAll, setGeneratingAll] = useState(false);
  const [similar, setSimilar] = useState(null);
  const [message, setMessage] = useState(null);

  const load = useCallback(async () => {
    try {
      setBrands(await brandsAPI.getAll());
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  const [seoJob, setSeoJob] = useState(null);
  useEffect(() => {
    if (!seoJob?.running) return undefined;
    const id = setInterval(async () => {
      try {
        const st = await brandsAPI.seoStatus();
        setSeoJob(st.job);
        if (!st.job?.running) { clearInterval(id); await load(); setMessage({ type: st.job?.errors?.length ? 'error' : 'success', text: `SEO marques (IA) : ${st.job?.done || 0} bloc(s) rédigé(s) ou traduit(s)${st.job?.errors?.length ? ` · ${st.job.errors.length} erreur(s)` : ''}` }); }
      } catch { /* retry */ }
    }, 3000);
    return () => clearInterval(id);
  }, [seoJob?.running, load]);
  const startSeoJob = async (starter) => {
    setMessage(null);
    try { const r = await starter(); setSeoJob(r.job); if (!r.started) setMessage({ type: 'error', text: r.message }); } catch (e) { setMessage({ type: 'error', text: e.response?.data?.detail || 'Lancement impossible' }); }
  };
  const fillAllSeo = () => { if (window.confirm(`Rédiger (FR) puis traduire en EN / DE / ES / IT et enregistrer le SEO des ${brands.filter(b => (b.seo_langs || 0) < 5).length} marque(s) incomplètes ? Les textes existants sont conservés. Tâche de fond, alerte admin à la fin.`)) startSeoJob(() => brandsAPI.fillAllSeo(false)); };
  const fillOneSeo = () => startSeoJob(() => brandsAPI.fillSeo(editing.name));
  const [rewrite, setRewrite] = useState(null);
  const loadRewrite = useCallback(() => brandsAPI.rewriteSeoStatus().then(setRewrite).catch(() => {}), []);
  useEffect(() => { loadRewrite(); }, [loadRewrite]);
  useEffect(() => {
    if (!rewrite?.job?.running) return undefined;
    const id = setInterval(async () => {
      const st = await brandsAPI.rewriteSeoStatus().catch(() => null);
      if (!st) return;
      setRewrite(st);
      if (!st.job?.running) { clearInterval(id); await load(); setMessage({ type: st.job?.errors?.length ? 'error' : 'success', text: `Marques réécrites par IA : ${st.job?.done || 0}/${st.job?.total || 0} · ${st.job?.flagged?.length || 0} encore sous 75 %${st.job?.errors?.length ? ` · ${st.job.errors.length} erreur(s)` : ''}` }); }
    }, 3000);
    return () => clearInterval(id);
  }, [rewrite?.job?.running, load]);
  const rewriteWeak = async () => {
    if (!window.confirm(`Réécrire par IA les ${rewrite?.weak || 0} page(s) marque trop semblables (textes FR remplacés, traductions refaites en EN / DE / ES / IT) ? Tâche de fond (≈ 40 s par marque), alerte admin à la fin.`)) return;
    setMessage(null);
    try { const r = await brandsAPI.rewriteSeoBulk(true); setRewrite(st => ({ ...(st || {}), job: r.job })); if (!r.started) setMessage({ type: 'error', text: r.message }); }
    catch (e) { setMessage({ type: 'error', text: e.response?.data?.detail || 'Lancement impossible' }); }
  };

  const openEdit = (b) => {
    setEditing(b);
    setForm(toForm(b));
    setMessage(null);
    setSimilar(null);
    brandsAPI.seoCheck(b.name).then(r => setSimilar(r.most_similar)).catch(() => {});
  };
  const openNew = () => {
    const name = newName.trim().toUpperCase();
    if (name.length < 2) { setMessage({ type: 'error', text: 'Nom de marque trop court' }); return; }
    setNewName('');
    openEdit({ name, product_count: 0 });
  };

  const generate = async (mainProduct) => {
    setGenerating(true);
    try {
      const g = await brandsAPI.generateSeo(editing.name, mainProduct);
      setForm(f => ({ ...f, ...g }));
      setMessage({ type: 'success', text: 'Texte type généré : relisez et personnalisez avant d\'enregistrer.' });
    } catch (e) {
      setMessage({ type: 'error', text: e.response?.data?.detail || 'Génération impossible' });
    } finally {
      setGenerating(false);
    }
  };

  const save = async () => {
    setSaving(true);
    setMessage(null);
    try {
      await brandsAPI.save(editing.name, form);
      await load();
      await loadBrandLogos(true);
      setEditing(null);
      setMessage({ type: 'success', text: `Marque ${editing.name} enregistrée` });
    } catch (e) {
      setMessage({ type: 'error', text: e.response?.data?.detail || "Erreur lors de l'enregistrement" });
    } finally {
      setSaving(false);
    }
  };

  const remove = async (b) => {
    if (b.product_count > 0) { setMessage({ type: 'error', text: `${b.name} : ${b.product_count} produit(s) utilisent encore cette marque. Supprimez-les ou changez leur marque avant de la supprimer.` }); setEditing(null); return; }
    if (!window.confirm(`Supprimer la marque ${b.name} (logo, texte SEO et page /marque) ?`)) return;
    setSaving(true);
    try {
      await brandsAPI.remove(b.name);
      await load();
      await loadBrandLogos(true);
      setEditing(null);
      setMessage({ type: 'success', text: `Marque ${b.name} supprimée` });
    } catch (e) {
      setMessage({ type: 'error', text: e.response?.data?.detail || 'Suppression impossible' });
    } finally {
      setSaving(false);
    }
  };

  const seoScore = (b) => seoChecks(toForm(b), null).filter(c => c.ok).length;
  const emptyBrands = brands.filter(b => !b.h1 && !b.description);

  const generateAll = async () => {
    if (!window.confirm(`Générer un texte SEO type pour ${emptyBrands.length} marque(s) sans contenu ? Les marques déjà renseignées ne seront pas modifiées.`)) return;
    setGeneratingAll(true);
    setMessage(null);
    try {
      const r = await brandsAPI.generateAll();
      await load();
      setMessage({ type: 'success', text: `${r.count} marque(s) générée(s)${r.skipped.length ? ` · ${r.skipped.length} déjà renseignée(s) conservée(s)` : ''}. Relisez et personnalisez les textes.` });
    } catch (e) {
      setMessage({ type: 'error', text: e.response?.data?.detail || 'Génération impossible' });
    } finally {
      setGeneratingAll(false);
    }
  };
  const visible = brands.filter(b => b.name.toLowerCase().includes(filter.toLowerCase()));
  const withLogo = brands.filter(b => b.logo).length;
  const withSeo = brands.filter(b => b.h1 && b.description).length;

  return (
    <>
      <BrandLogoFetchCard onChanged={load} />
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Marques de pièces ({brands.length})</h1>
          <p className="text-sm text-gray-500" data-testid="brands-logo-count">{withLogo} logo{withLogo > 1 ? 's' : ''} · {withSeo} page{withSeo > 1 ? 's' : ''} optimisée{withSeo > 1 ? 's' : ''} SEO</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button data-testid="brands-generate-all-button" onClick={generateAll} disabled={generatingAll || emptyBrands.length === 0} variant="outline" className="border-orange-400 text-orange-700 hover:bg-orange-50" title="Génère un brouillon SEO pour chaque marque encore vide">
            {generatingAll ? <Loader2 size={16} className="mr-1 animate-spin" /> : <Wand2 size={16} className="mr-1" />} Générer toutes les marques ({emptyBrands.length})
          </Button>
          <Button data-testid="brands-fill-seo-button" onClick={fillAllSeo} disabled={!!seoJob?.running || brands.filter(b => (b.seo_langs || 0) < 5).length === 0} variant="outline" className="border-emerald-400 text-emerald-700 hover:bg-emerald-50" title="Rédige (FR) puis traduit en EN / DE / ES / IT et enregistre le SEO de chaque page marque incomplète">
            {seoJob?.running ? <Loader2 size={16} className="mr-1 animate-spin" /> : <Wand2 size={16} className="mr-1" />} {seoJob?.running ? `SEO marques… ${seoJob.processed || 0}/${seoJob.total || '?'}` : `SEO marques (IA) (${brands.filter(b => (b.seo_langs || 0) < 5).length})`}
          </Button>
          <Button data-testid="brands-rewrite-weak-button" onClick={rewriteWeak} disabled={!!rewrite?.job?.running || !rewrite?.weak} variant="outline" className="border-violet-400 text-violet-700 hover:bg-violet-50" title={`Marques dont le texte est < ${rewrite?.threshold || 75} % unique (gabarit) : réécriture IA factuelle et unique, puis traduction`}>
            {rewrite?.job?.running ? <Loader2 size={16} className="mr-1 animate-spin" /> : <Sparkles size={16} className="mr-1" />} {rewrite?.job?.running ? `Réécriture… ${rewrite.job.processed || 0}/${rewrite.job.total || '?'} (${rewrite.job.detail || ''})` : `Marques uniques (IA) (${rewrite?.weak ?? '…'})`}
          </Button>
          <Input data-testid="brand-new-name-input" value={newName} onChange={(e) => setNewName(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') openNew(); }} placeholder="Nouvelle marque (ex : BREMBO)" className="w-56" />
          <Button data-testid="brand-new-button" onClick={openNew} className="bg-orange-500 hover:bg-orange-600"><Plus size={16} className="mr-1" /> Ajouter</Button>
        </div>
      </div>

      {message && !editing && (
        <p data-testid="brands-message" className={`mb-4 text-sm flex items-center gap-2 ${message.type === 'error' ? 'text-red-600' : 'text-green-700'}`}>
          {message.type === 'error' ? <AlertCircle size={16} /> : <CheckCircle size={16} />} {message.text}
        </p>
      )}

      <div className="relative mb-4 max-w-sm">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <Input data-testid="brands-filter-input" value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Filtrer les marques…" className="pl-9" />
      </div>

      {isLoading ? (
        <div className="flex justify-center py-12"><Loader2 className="animate-spin text-orange-500" /></div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="brands-grid">
          {visible.map(b => {
            const score = seoScore(b);
            return (
              <div key={b.name} className="relative group">
              <button
                type="button"
                data-testid={`brand-card-${b.name}`}
                onClick={() => openEdit(b)}
                className="w-full bg-white rounded-lg shadow-sm p-4 flex items-center gap-4 text-left hover:shadow-md hover:ring-2 hover:ring-orange-200 transition-all"
              >
                <div className="w-20 h-12 flex items-center justify-center bg-gray-50 rounded border border-gray-100 flex-shrink-0">
                  {b.logo ? <img src={uploadsAPI.resolveUrl(b.logo)} alt={b.name} className="max-h-10 max-w-[72px] object-contain no-placeholder" /> : <ImageOff size={18} className="text-gray-300" />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-gray-900 truncate">{b.name}</p>
                  <p className="text-xs text-gray-500 truncate">{b.product_count} produit{b.product_count > 1 ? 's' : ''}{b.slug ? ` · /marque/${b.slug}` : ''}</p>
                </div>
                <div className="flex flex-col items-end gap-1">
                  <span className={`text-xs px-2 py-0.5 rounded-full ${b.logo ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-500'}`}>{b.logo ? 'Logo' : 'Sans logo'}</span>
                  <span data-testid={`brand-seo-badge-${b.name}`} className={`text-[10px] px-2 py-0.5 rounded-full ${score >= 9 ? 'bg-green-100 text-green-700' : score >= 4 ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-500'}`}>SEO {score}/11</span>
                  <span data-testid={`brand-langs-badge-${b.name}`} className={`text-[10px] px-2 py-0.5 rounded-full ${(b.seo_langs || 0) >= 5 ? 'bg-sky-100 text-sky-700' : 'bg-gray-100 text-gray-500'}`}>{b.seo_langs || 0}/5 langues</span>
                </div>
              </button>
              <button type="button" onClick={() => remove(b)} title={b.product_count > 0 ? `${b.product_count} produit(s) — supprimez-les d'abord` : 'Supprimer la marque'} className={`absolute -top-2 -right-2 hidden group-hover:flex items-center justify-center w-7 h-7 rounded-full bg-white shadow border ${b.product_count > 0 ? 'text-gray-300 border-gray-200 cursor-not-allowed' : 'text-red-500 border-red-200 hover:bg-red-50'}`} data-testid={`brand-delete-${b.name}`}><Trash2 size={13} /></button>
              </div>
            );
          })}
          {visible.length === 0 && <p className="text-sm text-gray-500 col-span-full">Aucune marque ne correspond.</p>}
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[92vh] flex flex-col overflow-hidden" data-testid="brand-form">
            <div className="p-5 border-b bg-gradient-to-r from-orange-500 to-orange-600 text-white flex items-center justify-between">
              <h2 className="text-lg font-bold flex items-center gap-2" data-testid="brand-form-title"><Tag size={20} /> Marque {editing.name}</h2>
              <div className="flex items-center gap-3">
                <Link to={brandPath({ name: editing.name, slug: form.slug })} target="_blank" className="text-xs text-white/90 hover:text-white inline-flex items-center gap-1" data-testid="brand-page-link"><ExternalLink size={12} /> Voir la page</Link>
                <button onClick={() => setEditing(null)} className="text-white/80 hover:text-white"><X size={22} /></button>
              </div>
            </div>
            <div className="p-6 overflow-y-auto flex-1">
              {message && (
                <p data-testid="brand-form-message" className={`mb-4 text-sm flex items-center gap-2 ${message.type === 'error' ? 'text-red-600' : 'text-green-700'}`}>
                  {message.type === 'error' ? <AlertCircle size={16} /> : <CheckCircle size={16} />} {message.text}
                </p>
              )}
              <BrandSeoForm form={form} setForm={setForm} brandName={editing.name} onGenerate={generate} generating={generating} similar={similar} onFill={fillOneSeo} filling={!!seoJob?.running} langs={brands.find(b => b.name === editing.name)?.seo_langs || 0} />
            </div>
            <div className="p-4 border-t bg-gray-50 flex items-center gap-3">
              <Button variant="outline" onClick={() => remove(editing)} disabled={saving || editing.product_count > 0} title={editing.product_count > 0 ? `${editing.product_count} produit(s) utilisent cette marque` : 'Supprimer la marque'} className="text-red-600 border-red-200 hover:bg-red-50 mr-auto" data-testid="brand-form-delete"><Trash2 size={16} className="mr-2" /> Supprimer{editing.product_count > 0 ? ` (${editing.product_count} produits)` : ''}</Button>
              <Button variant="outline" onClick={() => setEditing(null)} data-testid="brand-form-cancel">Annuler</Button>
              <Button data-testid="brand-form-submit" onClick={save} disabled={saving} className="bg-green-600 hover:bg-green-700">
                {saving ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Check size={16} className="mr-2" />} Enregistrer
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AdminBrandsPanel;
